import { EmojiDictionaryEntry } from '../types'
import { downloadTextFile } from '../utils/fileUtils'
import dictionary from '../assets/emojiDictionary.txt?raw'

export class EmojiDictionary {
	private static readonly STORAGE_KEY = 'memtastic-emoji-dictionary'

	// In-memory cache for performance
	private static cache: Map<string, string> | null = null

	// Default emoji mappings in user-friendly text format
	private static readonly DEFAULT_TEXT = dictionary

	private static parseText(text: string): EmojiDictionaryEntry[] {
		const entries: EmojiDictionaryEntry[] = []
		const lines = text.split('\n')

		for (const line of lines) {
			const trimmed = line.trim()
			// Skip comments and empty lines
			if (!trimmed || trimmed.startsWith('#')) continue

			const match = trimmed.match(/^(.+?)\s*=\s*(.+)$/)
			if (match) {
				const [, wordsStr, emoji] = match
				const words = wordsStr.split(',').map(w => w.trim()).filter(w => w.length > 0)

				// Create an entry for each word
				for (const word of words) {
					entries.push({
						word: word.toLowerCase(),
						emoji: emoji.trim(),
					})
				}
			}
		}

		return entries
	}

	private static entriesToText(entries: EmojiDictionaryEntry[]): string {
		const header = `# Memtastic Emoji Dictionary
# Format: word = emoji (or word1, word2 = emoji for multiple words)
# Lines starting with # are comments and will be ignored

`
		// Group words by emoji
		const emojiGroups = new Map<string, string[]>()
		for (const entry of entries) {
			if (!emojiGroups.has(entry.emoji)) {
				emojiGroups.set(entry.emoji, [])
			}
			emojiGroups.get(entry.emoji)!.push(entry.word)
		}

		// Convert groups to lines, sorting words within each group
		const lines: string[] = []
		for (const [emoji, words] of emojiGroups) {
			const sortedWords = words.sort((a, b) => a.localeCompare(b))
			lines.push(`${sortedWords.join(', ')} = ${emoji}`)
		}

		// Sort lines by first word in each group
		lines.sort((a, b) => {
			const firstWordA = a.split(' = ')[0].split(',')[0].trim()
			const firstWordB = b.split(' = ')[0].split(',')[0].trim()
			return firstWordA.localeCompare(firstWordB)
		})

		return header + lines.join('\n')
	}


	// Build cache from current entries
	private static buildCache(): Map<string, string> {
		const entries = this.getEntries()
		const cache = new Map<string, string>()
		for (const entry of entries) {
			cache.set(entry.word.toLowerCase(), entry.emoji)
		}
		return cache
	}

	// Get raw text from storage
	static getText(): string {
		const stored = localStorage.getItem(this.STORAGE_KEY)
		return stored || this.DEFAULT_TEXT
	}

	// Save raw text to storage and clear cache
	static saveText(text: string): void {
		localStorage.setItem(this.STORAGE_KEY, text)
		this.cache = null // Clear cache to force rebuild
	}

	// Get entries as array (for UI)
	static getEntries(): EmojiDictionaryEntry[] {
		const text = this.getText()
		return this.parseText(text)
	}

	// Save entries as text
	static saveEntries(entries: EmojiDictionaryEntry[]): void {
		const text = this.entriesToText(entries)
		this.saveText(text)
	}

	// Add or update entries (supports comma-separated words)
	static addEntry(wordsStr: string, emoji: string): void {
		const entries = this.getEntries()
		const words = wordsStr.split(',').map(w => w.trim().toLowerCase()).filter(w => w.length > 0)

		// Remove existing entries for these words
		const filteredEntries = entries.filter(entry =>
			!words.includes(entry.word.toLowerCase()),
		)

		// Add new entries for all words
		for (const word of words) {
			filteredEntries.push({ word, emoji: emoji.trim() })
		}

		this.saveEntries(filteredEntries)
	}

	// Remove an entry
	static removeEntry(word: string): void {
		const entries = this.getEntries()
		const filtered = entries.filter(entry =>
			entry.word.toLowerCase() !== word.toLowerCase(),
		)
		this.saveEntries(filtered)
	}

	// Fast emoji lookup (uses cache)
	static getEmojiForWord(word: string): string | null {
		if (!this.cache) {
			this.cache = this.buildCache()
		}
		return this.cache.get(word.toLowerCase().replace('’', "'")) || null
	}

	// Reset to defaults
	static resetToDefaults(): void {
		this.saveText(this.DEFAULT_TEXT)
	}

	// Export dictionary as downloadable text
	static exportAsText(): string {
		return this.getText()
	}

	// Import dictionary from text (validates format)
	static importFromText(text: string): { success: boolean; error?: string } {
		try {
			const entries = this.parseText(text)
			if (entries.length === 0) {
				return { success: false, error: 'No valid entries found in text' }
			}
			this.saveText(text)
			return { success: true }
		} catch (error) {
			return { success: false, error: 'Failed to parse dictionary text' }
		}
	}

	// Download dictionary as a .txt file
	static downloadAsTxt(): void {
		const text = this.getText()
		downloadTextFile(text, 'memtastic-emoji-dictionary.txt')
	}
}
